import { FormFieldController, FormFieldState } from "@itk/react-form";
import { Button } from "@itk/mui-components";
import { Stack, TextField } from "@mui/material";
import { useState } from "react";
import { Shared_AttributeColorTypes } from "src/backend/openapi/v1";
import { ChosenSystem, SelectedColor } from "src/components/color-picker-types";
import { GenericDialog } from "src/components/generic-dialog";
import { NcsColorPicker } from "src/components/ncs-color-picker";
import { RalColorPicker } from "src/components/ral-color-picker";
import { attributeColorPickerK, useAttributeColorPickerT } from "src/translation-hooks/attribute-color-picker";
import { useNcsColorPickerT } from "src/translation-hooks/ncs-color-picker";

// RAL codes always start with "RAL " (e.g. "RAL 9010"); anything else stored on a Both-type
// attribute is assumed to be an NCS code - there's no other color system in play here
const getInitialChosenSystem = (value: string | null | undefined): ChosenSystem => {
  if (!value) return null;
  return value.startsWith("RAL ") ? "ral" : "ncs";
};

type Props = {
  formFieldState: FormFieldState<string>;
  formFieldController: FormFieldController<string>;
  disabled?: boolean;
  onChange?: (color: SelectedColor | null) => void;
  label?: string;
  colorType: Shared_AttributeColorTypes | null | undefined;
};

export const AttributeColorPicker = (props: Props) => {
  const { formFieldState, formFieldController, disabled, onChange, label, colorType } = props;
  const attributeColorPickerT = useAttributeColorPickerT();
  const ncsColorPickerT = useNcsColorPickerT();

  // only ever computed once from the field's initial value - a user actively switching systems
  // mid-edit shouldn't get overridden by this heuristic re-running as formFieldState.value changes
  const [chosenSystem, setChosenSystem] = useState<ChosenSystem>(() => getInitialChosenSystem(formFieldState.value));
  // kept independent of chosenSystem so "change color system" can reopen the chooser without first
  // nulling out chosenSystem and flashing an intermediate empty state
  const [isChooserOpen, setIsChooserOpen] = useState(false);
  // only set true right before setChosenSystem in the chooser's onClick handlers - stays false when
  // chosenSystem instead comes from getInitialChosenSystem on a normal mount/edit
  const [openPickerOnMount, setOpenPickerOnMount] = useState(false);

  const pickerProps = { formFieldState, formFieldController, disabled, onChange, label };

  if (colorType === Shared_AttributeColorTypes.Ncs) {
    return <NcsColorPicker {...pickerProps} />;
  }

  if (colorType === Shared_AttributeColorTypes.Ral) {
    return <RalColorPicker {...pickerProps} />;
  }

  if (colorType === Shared_AttributeColorTypes.Both) {
    return (
      <>
        {chosenSystem === null ? (
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            label={label}
            placeholder={ncsColorPickerT.noColorSelected}
            value=""
            disabled={disabled}
            onClick={() => setIsChooserOpen(true)}
            slotProps={{ input: { readOnly: true } }}
            sx={{ cursor: "pointer" }}
          />
        ) : (
          <Stack gap={0.5}>
            {chosenSystem === "ncs" ? <NcsColorPicker {...pickerProps} openOnMount={openPickerOnMount} /> : <RalColorPicker {...pickerProps} openOnMount={openPickerOnMount} />}
            <Button t={attributeColorPickerT} k={attributeColorPickerK} field="changeColorSystem" variant="text" size="small" onClick={() => setIsChooserOpen(true)} sx={{ alignSelf: "flex-start" }} />
          </Stack>
        )}

        {isChooserOpen && (
          <GenericDialog headerTitle={attributeColorPickerT.chooseColorSystemTitle} onClose={() => setIsChooserOpen(false)}>
            <Stack direction="row" gap={1}>
              <Button
                t={attributeColorPickerT}
                k={attributeColorPickerK}
                field="chooseNcsColor"
                variant="outlined"
                onClick={() => {
                  setOpenPickerOnMount(true);
                  setChosenSystem("ncs");
                  setIsChooserOpen(false);
                }}
              />
              <Button
                t={attributeColorPickerT}
                k={attributeColorPickerK}
                field="chooseRalColor"
                variant="outlined"
                onClick={() => {
                  setOpenPickerOnMount(true);
                  setChosenSystem("ral");
                  setIsChooserOpen(false);
                }}
              />
            </Stack>
          </GenericDialog>
        )}
      </>
    );
  }

  return null;
};
